/**
 * Forge Vision — live analyze experience.
 * Extracted from the (deleted) marketing Home so it can be used inside
 * the app subdomain via `/embed/forge-vision` and other authenticated
 * surfaces without dragging in the marketing site.
 */
import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Copy, Check } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar } from "recharts";
import ForgeVisionLoader from "@/components/forge-vision/ForgeVisionLoader";
import { marketingUrl } from "@/lib/marketingUrl";

export function ForgeVisionExperience() {
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const [suggesting, setSuggesting] = useState(false);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        const t = setTimeout(async () => {
            const q = query.trim();
            if (q.length < 3 || selected?.display === q) { setSuggestions([]); setSuggestOpen(false); return; }
            setSuggesting(true);
            try {
                const { data } = await api.get(`/public/forge-vision/address-suggest?q=${encodeURIComponent(q)}`);
                setSuggestions(data.suggestions || []);
                setSuggestOpen((data.suggestions || []).length > 0);
                setHighlight(-1);
            } catch { /* silent */ }
            finally { setSuggesting(false); }
        }, 250);
        return () => clearTimeout(t);
    }, [query, selected]);

    const chooseSuggestion = (s) => { setSelected(s); setQuery(s.display); setSuggestions([]); setSuggestOpen(false); };
    const onKey = (e) => {
        if (!suggestOpen || suggestions.length === 0) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
        else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); chooseSuggestion(suggestions[highlight]); }
        else if (e.key === "Escape") { setSuggestOpen(false); }
    };
    const submit = async (e) => {
        e.preventDefault();
        const q = query.trim();
        if (!q) return;
        setLoading(true); setResult(null); setSuggestOpen(false);
        const [{ data }] = await Promise.all([
            (async () => {
                try {
                    const payload = { query: q };
                    if (selected?.lat) { payload.lat = selected.lat; payload.lng = selected.lng; }
                    return await api.post("/public/forge-vision/analyze", payload);
                } catch (err) {
                    toast.error(err?.response?.data?.detail || "Analysis failed");
                    return { data: null };
                }
            })(),
            new Promise((r) => setTimeout(r, 4500)),
        ]);
        if (data) setResult(data);
        setLoading(false);
    };
    const shareUrl = result?.share_slug ? `${window.location.origin}/forge-vision/r/${result.share_slug}` : null;
    const copyShare = async () => {
        if (!shareUrl) return;
        try { await navigator.clipboard.writeText(shareUrl); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1800); } catch { toast.error("Copy failed"); }
    };

    return (
        <section id="forge-vision" className="section border-t border-arch-white/10 bg-obsidian" data-testid="forge-vision-section">
            <div className="container-editorial">
                <div className="max-w-3xl">
                    <div className="chip chip-ai" data-testid="fv-chip"><Sparkles size={10} /> Forge Vision · Live Demo</div>
                    <h2 className="mt-6 font-display text-4xl md:text-6xl tracking-tight leading-[1.05] italic">Analyze Before You Build.</h2>
                    <p className="mt-6 text-lg text-arch-white/60">Enter any property address, parcel ID, or development opportunity. Forge Vision returns a full pre-underwriting snapshot in seconds.</p>
                </div>

                <form onSubmit={submit} className="mt-10 max-w-2xl relative" data-testid="forge-vision-form" role="search" aria-label="Analyze a property">
                    <div className="glass p-2 flex items-center gap-2">
                        <input
                            ref={inputRef}
                            value={query}
                            onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
                            onKeyDown={onKey}
                            placeholder="1600 Amphitheatre Parkway, Mountain View · or a parcel ID"
                            className="flex-1 bg-transparent text-arch-white placeholder:text-arch-white/40 px-4 py-3 text-sm focus:outline-none"
                            data-testid="forge-vision-input"
                            aria-autocomplete="list"
                            aria-expanded={suggestOpen}
                            aria-controls="fv-suggestions"
                        />
                        {suggesting && <motion.div className="w-2 h-2 rounded-full bg-ice mx-2" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity }} data-testid="forge-vision-suggesting" />}
                        <button type="submit" disabled={loading} className="bg-navy border border-ice/50 text-arch-white px-5 py-3 text-sm hover:bg-navy/80 transition-colors disabled:opacity-50" data-testid="forge-vision-submit">
                            {loading ? "Analyzing…" : "Analyze"}
                        </button>
                    </div>
                    {suggestOpen && suggestions.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
                            id="fv-suggestions" role="listbox"
                            className="absolute left-0 right-0 top-full mt-1 border border-ice/30 bg-obsidian/95 backdrop-blur z-20 max-h-72 overflow-y-auto"
                            data-testid="forge-vision-suggestions"
                        >
                            <div className="sr-only" aria-live="polite">{suggestions.length} suggestions</div>
                            {suggestions.map((s, i) => (
                                <button
                                    key={i} type="button" onClick={() => chooseSuggestion(s)} onMouseEnter={() => setHighlight(i)}
                                    className={`w-full text-left px-4 py-3 border-b border-arch-white/5 last:border-b-0 text-sm ${i === highlight ? "bg-ice/10 text-arch-white" : "text-arch-white/80 hover:bg-arch-white/5"}`}
                                    data-testid={`fv-suggestion-${i}`}
                                    role="option" aria-selected={i === highlight}
                                >
                                    <div className="font-display text-arch-white">{s.address_line || s.display}</div>
                                    <div className="text-[11px] text-arch-white/50 mt-0.5">{[s.city, s.state, s.country].filter(Boolean).join(" · ")}</div>
                                </button>
                            ))}
                        </motion.div>
                    )}
                    <div className="mt-2 text-[10px] text-arch-white/40" data-testid="forge-vision-attribution">Data © OpenStreetMap contributors</div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-arch-white/40">
                        {["1600 Amphitheatre Parkway", "123 Main Street", "40-acre parcel, Colorado"].map((s) => (
                            <button key={s} type="button" onClick={() => { setQuery(s); setSelected(null); }} className="border border-arch-white/10 px-2.5 py-1 hover:border-ice/60 hover:text-ice" data-testid={`forge-vision-sample-${s.split(' ')[0].toLowerCase()}`}>{s}</button>
                        ))}
                    </div>
                </form>

                {loading && <div className="mt-10 max-w-3xl"><ForgeVisionLoader /></div>}

                {result && <ForgeVisionResults result={result} shareUrl={shareUrl} onCopy={copyShare} copied={copied} />}
            </div>
        </section>
    );
}

function ForgeVisionResults({ result, shareUrl, onCopy, copied }) {
    return (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-10" data-testid="forge-vision-results">
            <div className="flex items-center flex-wrap gap-2 mb-4">
                <div className="chip chip-ai"><Sparkles size={10} /> Demonstration Analysis — not investment advice</div>
                {shareUrl && (
                    <button onClick={onCopy} className="ml-auto inline-flex items-center gap-2 border border-ice/40 text-ice px-3 py-1.5 text-xs hover:bg-ice/10" data-testid="forge-vision-share">
                        {copied ? <><Check size={12}/> Copied</> : <><Copy size={12}/> Share this analysis</>}
                    </button>
                )}
            </div>
            <div className="border border-arch-white/10 p-6 md:p-8">
                <div className="grid md:grid-cols-3 gap-6">
                    <div className="md:col-span-2">
                        <div className="overline text-arch-white/40">Property Summary</div>
                        <div className="mt-2 font-display text-2xl">{result.query}</div>
                        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                            <Row label="Parcel" v={result.property.parcel_number} />
                            <Row label="Zoning" v={result.property.zoning} />
                            <Row label="Lot sqft" v={result.property.lot_size_sqft} />
                            <Row label="Timeline (mo)" v={result.property.estimated_timeline_months} />
                        </div>
                    </div>
                    <div>
                        <div className="overline text-arch-white/40">Opportunity Score</div>
                        <ResponsiveContainer width="100%" height={140}>
                            <RadialBarChart innerRadius="50%" outerRadius="100%" data={[{ name: "score", value: result.property.opportunity_score, fill: "#DCEEFF" }]} startAngle={225} endAngle={-45}>
                                <RadialBar dataKey="value" background={{ fill: "rgba(247,247,244,0.05)" }} />
                            </RadialBarChart>
                        </ResponsiveContainer>
                        <div className="text-center font-display text-3xl text-ice -mt-16 mb-8">{result.property.opportunity_score}</div>
                    </div>
                </div>

                <div className="mt-8 grid md:grid-cols-4 gap-3">
                    <Tile label="Estimated land value" value={`$${result.property.estimated_land_value.toLocaleString()}`} />
                    <Tile label="Estimated build cost" value={`$${result.property.estimated_build_cost.toLocaleString()}`} />
                    <Tile label="Estimated completed value" value={`$${result.property.estimated_completed_value.toLocaleString()}`} />
                    <Tile label="Expected ROI" value={`${result.property.expected_roi_pct}%`} />
                </div>

                <div className="mt-8 grid md:grid-cols-2 gap-6">
                    <div>
                        <div className="overline text-arch-white/40">Highest & Best Use</div>
                        <div className="mt-2 font-display text-xl text-ice">{result.property.highest_and_best_use}</div>
                        <div className="mt-4 overline text-arch-white/40">Suggested Development</div>
                        <div className="mt-2 text-sm">{result.property.suggested_development}</div>
                    </div>
                    <div>
                        <div className="overline text-arch-white/40">AI Summary</div>
                        <div className="mt-2 text-sm text-arch-white/80 leading-relaxed">{result.narrative.summary}</div>
                    </div>
                </div>

                <div className="mt-8 grid md:grid-cols-2 gap-6">
                    <BulletsBlock title="Recommendations" items={result.narrative.recommendations} />
                    <BulletsBlock title="Risks" items={result.narrative.risks} />
                    <BulletsBlock title="Opportunities" items={result.narrative.opportunities} />
                    <div className="border border-arch-white/10 p-4">
                        <div className="overline text-arch-white/40">5-year projection</div>
                        <ResponsiveContainer width="100%" height={200}>
                            <AreaChart data={result.property.projection_5yr}>
                                <XAxis dataKey="year" stroke="rgba(247,247,244,0.4)" fontSize={10} />
                                <YAxis stroke="rgba(247,247,244,0.4)" fontSize={10} />
                                <Tooltip contentStyle={{ background: "#111315", border: "1px solid rgba(220,238,255,0.3)" }} />
                                <Area dataKey="projected_value" stroke="#DCEEFF" fill="#DCEEFF" fillOpacity={0.2} />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="mt-8 pt-6 border-t border-arch-white/10 text-xs text-arch-white/40 italic">{result.demonstration_notice}</div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
                <a href={marketingUrl("/")} className="bg-navy border border-ice/60 text-arch-white px-5 py-3 text-sm hover:bg-navy/80" data-testid="fv-cta-early-access">Request early access to ForgeOS</a>
                <a href={marketingUrl("/")} className="border border-arch-white/30 px-5 py-3 text-sm hover:bg-arch-white/5">See how ForgeOS works</a>
            </div>
        </motion.div>
    );
}

function Row({ label, v }) { return <div><div className="overline text-arch-white/40">{label}</div><div className="mt-1">{v}</div></div>; }
function Tile({ label, value }) {
    return (
        <div className="border border-arch-white/10 p-4">
            <div className="overline text-arch-white/40">{label}</div>
            <div className="mt-2 font-display text-xl text-ice">{value}</div>
        </div>
    );
}
function BulletsBlock({ title, items }) {
    return (
        <div className="border border-arch-white/10 p-4">
            <div className="overline text-arch-white/40">{title}</div>
            <ul className="mt-3 space-y-2 text-sm text-arch-white/80">{(items || []).map((it, i) => <li key={i} className="flex gap-2"><span className="text-ice mt-1">·</span><span>{it}</span></li>)}</ul>
        </div>
    );
}
